import { useEffect, useRef, useState } from 'react';
import { Eraser, PenLine, Type } from 'lucide-react';

export interface SignatureResult {
  dataUrl: string;
  method: 'drawn' | 'typed';
}

interface SignaturePadProps {
  /** Called when a signature is produced, or null when cleared. */
  onChange: (sig: SignatureResult | null) => void;
  /** Disable interaction (e.g. after it's been signed/locked). */
  disabled?: boolean;
}

const INK = '#0B1B3A';

export function SignaturePad({ onChange, disabled }: SignaturePadProps) {
  const [mode, setMode] = useState<'drawn' | 'typed'>('drawn');
  const [typed, setTyped] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  // Size the canvas to its box at device resolution for crisp lines.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || mode !== 'drawn') return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = INK;
    }
  }, [mode]);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  }
  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current && canvasRef.current) {
      onChange({ dataUrl: canvasRef.current.toDataURL('image/png'), method: 'drawn' });
    }
  }

  function clearDrawn() {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  }

  // Typed mode: render the name in a script font to a canvas → image.
  function commitTyped(value: string) {
    setTyped(value);
    if (!value.trim()) {
      onChange(null);
      return;
    }
    const c = document.createElement('canvas');
    c.width = 600;
    c.height = 160;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = INK;
    ctx.font = '52px "Snell Roundhand", "Brush Script MT", cursive';
    ctx.textBaseline = 'middle';
    ctx.fillText(value, 20, 90);
    onChange({ dataUrl: c.toDataURL('image/png'), method: 'typed' });
  }

  return (
    <div>
      <div className="mb-2 flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setMode('drawn');
            onChange(null);
          }}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
            mode === 'drawn' ? 'border-brand bg-brand text-white' : 'border-slate-300 text-ink-muted'
          }`}
        >
          <PenLine size={15} /> Draw
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setMode('typed');
            onChange(null);
          }}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
            mode === 'typed' ? 'border-brand bg-brand text-white' : 'border-slate-300 text-ink-muted'
          }`}
        >
          <Type size={15} /> Type
        </button>
      </div>

      {mode === 'drawn' ? (
        <div>
          <canvas
            ref={canvasRef}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            className="h-40 w-full touch-none rounded-lg border-2 border-dashed border-sky-200 bg-white"
            style={{ cursor: disabled ? 'default' : 'crosshair' }}
          />
          {!disabled && (
            <button
              type="button"
              onClick={clearDrawn}
              className="btn-secondary mt-2 inline-flex items-center gap-1.5 text-sm"
            >
              <Eraser size={15} /> Clear
            </button>
          )}
        </div>
      ) : (
        <div>
          <input
            className="input"
            placeholder="Type your full legal name"
            value={typed}
            disabled={disabled}
            onChange={(e) => commitTyped(e.target.value)}
          />
          {typed.trim() && (
            <div
              className="mt-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-navy-900"
              style={{ fontFamily: '"Snell Roundhand", "Brush Script MT", cursive', fontSize: 34 }}
            >
              {typed}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
